import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { reduxForm } from 'redux-form'
import { connect } from 'react-redux'
import ReactMarkdown from 'markdown-to-jsx'

import { onSubmitFail, submitAndCollateErrorsPromise } from '@ynap/form-utils'
import BEMHelper from '@ynap/bem-helper'
import Field from '@ynap/field'
import { SimpleAsyncButton } from '@ynap/button'
import { userActions } from '@ynap/user-state'
import Skeleton from '@ynap/skeleton'

import validate from './validate'

const bem = new BEMHelper('ConsentPhone')

export class ConsentPhone extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            hasPhoneNumber: false,
            didNotConsent: false,
            webAndAppButtonText: this.props.messages.consentPhone.goToNewWebsite(),
            webAndAppButtonUrl: this.props.messages.consentPhone.goToNewWebsiteUrl(),
        }
        this.onSubmit = this.onSubmit.bind(this)
        this.onDisagree = this.onDisagree.bind(this)
    }

    static propTypes = {
        form: PropTypes.string,
        user: PropTypes.object,
        messages: PropTypes.shape({
            consentPhone: PropTypes.shape({
                brand: PropTypes.func.isRequired,
                paragraph1: PropTypes.func.isRequired,
                paragraph2: PropTypes.func.isRequired,
                paragraph3: PropTypes.func.isRequired,
                listItem1: PropTypes.func.isRequired,
                listItem2: PropTypes.func.isRequired,
                listItem3: PropTypes.func.isRequired,
                placeholder: PropTypes.func.isRequired,
                agree: PropTypes.func.isRequired,
                disagree: PropTypes.func.isRequired,
                agreedThankYou: PropTypes.func.isRequired,
                disagreedThankYou: PropTypes.func.isRequired,
                agreedText: PropTypes.func.isRequired,
                disagreedText: PropTypes.func.isRequired,
                goToHomepage: PropTypes.func.isRequired,
                goToNewWebsite: PropTypes.func.isRequired,
                goToNewWebsiteUrl: PropTypes.func.isRequired,
                downloadNewApp: PropTypes.func.isRequired,
                forIOS: PropTypes.func.isRequired,
                forAndroid: PropTypes.func.isRequired,
                ccParagraph1: PropTypes.func.isRequired,
                ccParagraph2: PropTypes.func.isRequired,
                ccEmail: PropTypes.func.isRequired,
                chinaTelephone: PropTypes.func.isRequired,
                hongKongTelephone: PropTypes.func.isRequired,
                globalTelephone: PropTypes.func.isRequired,
            }).isRequired,
        }).isRequired,
        handleSubmit: PropTypes.func.isRequired,
        onSubmit: PropTypes.func.isRequired,
        locale: PropTypes.shape({
            id: PropTypes.string,
        }).isRequired,
    }

    componentDidMount() {
        const { user, fetchUser, messages } = this.props
        const { downloadNewApp, forIOS, forAndroid } = messages.consentPhone

        const userAgent = navigator.userAgent.toLowerCase()
        const isAndroid = userAgent.indexOf('android') > -1
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

        if (isAndroid) {
            this.setState({
                webAndAppButtonText: downloadNewApp(),
                webAndAppButtonUrl: forAndroid(),
            })
        }

        if (isIOS) {
            this.setState({
                webAndAppButtonText: downloadNewApp(),
                webAndAppButtonUrl: forIOS(),
            })
        }

        if (!user && fetchUser) {
            fetchUser()
        } else {
            this.setState({
                hasPhoneNumber: user?.phone1 || user?.mobilePhone1,
            })
        }
    }

    componentDidUpdate() {
        const { user } = this.props
        if (user) {
            this.setState({
                hasPhoneNumber: user?.phone1 || user?.mobilePhone1,
            })
        }
    }

    onSubmit(values) {
        const { form, updateUserInfo } = this.props
        const usrWithConsentAttr = {
            externalConsentsProvided: 'true',
            ...values,
            contextAttribute: [
                {
                    attributeName: 'maFengMaoMigrationConsent',
                    attributeValue: {
                        value: 'true',
                    },
                },
            ],
        }
        return submitAndCollateErrorsPromise(updateUserInfo(usrWithConsentAttr, form), values, this.props)
    }

    onDisagree() {
        window?.document?.querySelector('#consent-phone-banner')?.scrollIntoView()
        this.setState({
            didNotConsent: true,
        })
    }

    render() {
        const { didNotConsent, hasPhoneNumber, webAndAppButtonText, webAndAppButtonUrl } = this.state
        const { user, messages, form, locale, submitting, handleSubmit } = this.props
        const {
            consentPhone: {
                brand,
                paragraph1,
                paragraph2,
                paragraph3,
                listItem1,
                listItem2,
                listItem3,
                placeholder,
                agree,
                disagree,
                agreedThankYou,
                disagreedThankYou,
                agreedText,
                disagreedText,
                goToHomepage,
                ccParagraph1,
                ccParagraph2,
                ccEmail,
                chinaTelephone,
                hongKongTelephone,
                globalTelephone,
            },
        } = messages
        const skeleton = (
            <div className={bem('skeletonWrapper')}>
                <Skeleton count={1} align={'center'} className={bem('skeletonTitle')} />
                <Skeleton count={1} align={'center'} className={bem('skeletontext')} />
                <div className={bem('skeletonText')}>
                    <Skeleton count={4} align={'left'} />
                </div>
                <div className={bem('skeletonText')}>
                    <Skeleton count={4} align={'center'} />
                </div>
                <div className={bem('skeletonText')}>
                    <Skeleton count={4} align={'left'} />
                </div>
            </div>
        )

        const formPage = (
            <>
                <h2 className={bem('logo')}>{brand()}</h2>

                <p className={bem('text')}>{paragraph1()}</p>
                <p className={bem('text')}>{paragraph2()}</p>
                <ul className={bem('text')}>
                    <li>{listItem1()}</li>
                    <li>{listItem2()}</li>
                    <li>{listItem3()}</li>
                </ul>
                <p className={bem('text')}>
                    <ReactMarkdown
                        options={{
                            overrides: {
                                a: (props) => (
                                    <a
                                        className={bem('link')}
                                        href={props.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {props.children}
                                    </a>
                                ),
                            },
                        }}
                    >
                        {paragraph3()}
                    </ReactMarkdown>
                </p>

                <form id={form} className={bem('form')} onSubmit={handleSubmit(this.onSubmit)}>
                    <Field
                        className={bem('field')}
                        name="mobilePhone1"
                        type="text"
                        label={''}
                        autoComplete="mobilePhone1"
                        placeholder={placeholder()}
                    />
                    <SimpleAsyncButton className={bem('agree')} copy={agree()} loading={submitting} />
                </form>

                <div className={bem('disagree')} onClick={this.onDisagree}>
                    {disagree()}
                </div>
            </>
        )

        const thankYouPage = (
            <>
                {!didNotConsent && (
                    <div>
                        <i className={bem('confirmationIcon')}></i>
                        <div className={bem('agreedThankYou')}>{agreedThankYou()}</div>
                    </div>
                )}

                {didNotConsent && <div className={bem('disagreedThankYou')}>{disagreedThankYou()}</div>}

                <div className={bem('thankYouText')}>{didNotConsent ? disagreedText() : agreedText()}</div>

                <a className={bem('goToNewWebsite')} href={webAndAppButtonUrl} target="_blank" rel="noreferrer">
                    {webAndAppButtonText}
                </a>

                <a className={bem('goToHomepage')} href={`/${locale.id}/`}>
                    {goToHomepage()}
                </a>

                <div className={bem('ccWrapper')}>
                    <div>{ccParagraph1()}</div>
                    <div className={bem('ccText')}>{ccParagraph2()}</div>

                    <div className={bem('ccEmail')}>
                        {ccEmail()
                            .split(new RegExp('\\[|\\]'))
                            .filter((token) => !!token)
                            .map((token, index) => {
                                return token.includes('@') ? (
                                    <a key={`mail-${index}`} href={`mailto:${token}`}>
                                        {token}
                                    </a>
                                ) : (
                                    <span key={`mail-${index}`}>{token}</span>
                                )
                            })}
                    </div>

                    <ul>
                        <li className={bem('ccPhone')}>
                            {chinaTelephone()
                                .split(new RegExp('\\[|\\]'))
                                .filter((token) => !!token)
                                .map((token, index) => {
                                    return parseInt(token, 10) > 0 || token?.startsWith('+') ? (
                                        <a key={`phone-${index}`} href={`tel:${token}`}>
                                            {token}
                                        </a>
                                    ) : (
                                        <span key={`phone-${index}`}>{token}</span>
                                    )
                                })}
                        </li>
                        {!!hongKongTelephone() && (
                            <li className={bem('ccPhone')}>
                                <a href={`tel:+852 5808 1362`}>{hongKongTelephone()}</a>
                            </li>
                        )}
                        {!!globalTelephone() && (
                            <li className={bem('ccPhone')}>
                                <a href={`tel:+44 (0)330 022 5700`}>{globalTelephone()}</a>
                            </li>
                        )}
                    </ul>
                </div>
            </>
        )
        return (
            <>
                <div className={bem('banner')} id={'consent-phone-banner'}></div>
                {!user ? (
                    skeleton
                ) : (
                    <div className={bem('content')}>{!hasPhoneNumber && !didNotConsent ? formPage : thankYouPage}</div>
                )}
            </>
        )
    }
}

const ConsentPhoneContainer = connect((state) => ({ user: state.user }), { ...userActions })(ConsentPhone)

export const DecoratedConsentPhone = reduxForm({ form: 'consentPhone', validate, onSubmitFail })(ConsentPhoneContainer)
